import { db } from '../firebase';
import { collection, doc, getDoc, getDocs, setDoc, updateDoc, deleteDoc, query, where, limit } from 'firebase/firestore';

const AI_MODELS_COLLECTION = 'ai_models';
const USER_MODEL_SETTINGS_COLLECTION = 'user_model_settings';

/**
 * Fetch all available AI models defined by the admin
 * @returns {Promise<Array>} Array of AI model objects
 */
export const getAvailableAIModels = async () => {
  try {
    const modelsCollection = collection(db, AI_MODELS_COLLECTION);
    const modelsSnapshot = await getDocs(modelsCollection);
    
    const models = [];
    modelsSnapshot.forEach(doc => {
      models.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    return models;
  } catch (error) {
    console.error('Error fetching AI models:', error);
    throw error;
  }
};

/**
 * Get a specific AI model by ID
 * @param {string} modelId - The ID of the model to retrieve
 * @returns {Promise<Object>} The AI model object
 */
export const getAIModel = async (modelId) => {
  try {
    const modelRef = doc(db, AI_MODELS_COLLECTION, modelId);
    const modelDoc = await getDoc(modelRef);
    
    if (modelDoc.exists()) {
      return {
        id: modelDoc.id,
        ...modelDoc.data()
      };
    } else {
      throw new Error(`Model with ID ${modelId} not found`);
    }
  } catch (error) {
    console.error(`Error fetching AI model ${modelId}:`, error);
    throw error;
  }
};

/**
 * Get an AI model by its ID
 * @param {string} modelId - The model ID
 * @returns {Promise<Object|null>} - The model configuration or null if not found
 */
export const getAIModelById = async (modelId) => {
  try {
    if (!modelId) {
      console.error('Model ID is required');
      return null;
    }

    const modelRef = doc(db, 'ai_models', modelId);
    const modelDoc = await getDoc(modelRef);

    if (!modelDoc.exists()) {
      console.log(`Model with ID ${modelId} not found`);
      return null;
    }

    return {
      id: modelDoc.id,
      ...modelDoc.data()
    };
  } catch (error) {
    console.error('Error getting AI model by ID:', error);
    return null;
  }
};

/**
 * Create a new AI model (admin only)
 * @param {Object} model - The model to create
 * @returns {Promise<Object>} The created model
 */
export const createAIModel = async (model) => {
  try {
    const modelRef = doc(collection(db, AI_MODELS_COLLECTION));
    
    // Prepare the model data
    const modelData = {
      displayName: model.displayName,
      provider: model.provider,
      modelId: model.modelId,
      isEnabled: model.isEnabled !== false, // Default to true if not specified
      isDefault: model.isDefault || false,
      creditCostPerOutput: model.creditCostPerOutput || 1,
      temperature: model.temperature || 0.7,
      maxTokens: model.maxTokens || 2000,
      type: model.type || 'text', // Include model type (text/vision)
      maxImages: model.maxImages || 1, // Include maxImages for vision models
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      // Include API configuration directly in the model
      apiConfig: {
        apiKey: model.apiConfig?.apiKey || '',
        baseUrl: model.apiConfig?.baseUrl || ''
      }
    };
    
    // Set default base URL based on provider if not specified
    if (!modelData.apiConfig.baseUrl) {
      switch (model.provider) {
        case 'openai':
          modelData.apiConfig.baseUrl = 'https://api.openai.com/v1';
          break;
        case 'deepseek':
          modelData.apiConfig.baseUrl = 'https://api.deepseek.com/v1';
          break;
        case 'gemini':
          modelData.apiConfig.baseUrl = 'https://generativelanguage.googleapis.com/v1beta';
          break;
      }
    }
    
    await setDoc(modelRef, modelData);
    
    return {
      id: modelRef.id,
      ...model
    };
  } catch (error) {
    console.error('Error creating AI model:', error);
    throw error;
  }
};

/**
 * Update an existing AI model (admin only)
 * @param {string} modelId - The ID of the model to update
 * @param {Object} updates - The updates to apply
 * @returns {Promise<Object>} The updated model
 */
export const updateAIModel = async (modelId, updates) => {
  try {
    const modelRef = doc(db, AI_MODELS_COLLECTION, modelId);
    
    // Prepare the update data
    const updatedModel = {
      ...updates,
      updatedAt: new Date().toISOString()
    };
    
    // Ensure API configuration is properly structured
    if (updates.apiConfig) {
      updatedModel.apiConfig = {
        apiKey: updates.apiConfig.apiKey || '',
        baseUrl: updates.apiConfig.baseUrl || ''
      };
      
      // Set default base URL based on provider if not specified
      if (!updatedModel.apiConfig.baseUrl && updates.provider) {
        switch (updates.provider) {
          case 'openai':
            updatedModel.apiConfig.baseUrl = 'https://api.openai.com/v1';
            break;
          case 'deepseek':
            updatedModel.apiConfig.baseUrl = 'https://api.deepseek.com/v1';
            break;
          case 'gemini':
            updatedModel.apiConfig.baseUrl = 'https://generativelanguage.googleapis.com/v1beta';
            break;
        }
      }
    }
    
    await updateDoc(modelRef, updatedModel);
    
    return {
      id: modelId,
      ...updatedModel
    };
  } catch (error) {
    console.error(`Error updating AI model ${modelId}:`, error);
    throw error;
  }
};

/**
 * Delete an AI model (admin only)
 * @param {string} modelId - The ID of the model to delete
 * @returns {Promise<void>}
 */
export const deleteAIModel = async (modelId) => {
  try {
    const modelRef = doc(db, AI_MODELS_COLLECTION, modelId);
    await deleteDoc(modelRef);
  } catch (error) {
    console.error(`Error deleting AI model ${modelId}:`, error);
    throw error;
  }
};

/**
 * Get the default AI model
 * @returns {Promise<Object|null>} - The default model configuration or null if not found
 */
export const getDefaultAIModel = async () => {
  try {
    const modelsRef = collection(db, 'ai_models');
    const q = query(modelsRef, where('isDefault', '==', true), limit(1));
    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      console.log('No default model found');
      
      // Try to get any enabled model as fallback
      const fallbackQuery = query(modelsRef, where('isEnabled', '==', true), limit(1));
      const fallbackSnapshot = await getDocs(fallbackQuery);
      
      if (fallbackSnapshot.empty) {
        return null;
      }
      
      const fallbackDoc = fallbackSnapshot.docs[0];
      return {
        id: fallbackDoc.id,
        ...fallbackDoc.data()
      };
    }

    const defaultModelDoc = querySnapshot.docs[0];
    return {
      id: defaultModelDoc.id,
      ...defaultModelDoc.data()
    };
  } catch (error) {
    console.error('Error getting default AI model:', error);
    return null;
  }
};

/**
 * Calculate the credit cost for a template based on its output fields and selected model
 * @param {Object} template - The template object
 * @param {Object} model - The AI model object
 * @returns {number} The total credit cost
 */
export const calculateTemplateCreditCost = (template, model) => {
  if (!template || !model) {
    return 0;
  }
  
  // Tool templates don't use AI, so they cost 0 credits
  if (template.type === 'tool') {
    return 0;
  }
  
  // For sidebar-only mode in image templates, count as 1 output regardless of fields
  if (template.type === 'image' && template.sidebarOnly) {
    return 1 * (model.creditCostPerOutput || 1);
  }
  
  // Count the number of output fields
  const outputCount = template.outputFields ? template.outputFields.length : 0;
  
  // Calculate the total cost
  return outputCount * (model.creditCostPerOutput || 1);
};

/**
 * Calculate the total credit cost for a workflow
 * @param {Object} workflow - The workflow object
 * @param {Array} templates - Array of templates in the workflow
 * @param {Array} models - Array of AI models
 * @param {number} productCount - Number of products to process
 * @returns {Object} Credit cost breakdown and total
 */
export const calculateWorkflowCreditCost = (workflow, templates, models, productCount = 1) => {
  if (!workflow || !templates || templates.length === 0 || !models || models.length === 0) {
    return { total: 0, breakdown: {} };
  }
  
  const breakdown = {};
  let totalCost = 0;
  
  // For each template in the workflow
  workflow.templates.forEach(templateId => {
    const template = templates.find(t => t.id === templateId);
    if (template) {
      // Find the model associated with this template
      const modelId = template.modelId || workflow.defaultModelId;
      const model = models.find(m => m.id === modelId);
      
      if (model) {
        const templateCost = calculateTemplateCreditCost(template, model);
        breakdown[template.id] = {
          name: template.name,
          outputCount: template.type === 'tool' ? 0 : (template.outputFields?.length || 0),
          modelName: template.type === 'tool' ? 'Tool (No AI)' : model.displayName,
          costPerOutput: template.type === 'tool' ? 0 : model.creditCostPerOutput,
          templateCost: templateCost,
          totalCost: templateCost * productCount
        };
        
        // Only add to total cost if this is not a tool template
        if (template.type !== 'tool') {
          totalCost += templateCost * productCount;
        }
      }
    }
  });
  
  return {
    total: totalCost,
    breakdown,
    productCount
  };
};

/**
 * Get a user's model settings for a specific template
 * @param {string} userId - The user ID
 * @param {string} templateId - The template ID
 * @returns {Promise<Object>} The user's model settings for the template
 */
export const getUserTemplateModelSettings = async (userId, templateId) => {
  try {
    const settingsRef = doc(db, USER_MODEL_SETTINGS_COLLECTION, `${userId}_${templateId}`);
    const settingsDoc = await getDoc(settingsRef);
    
    if (settingsDoc.exists()) {
      return settingsDoc.data();
    } else {
      // Return null if no settings found
      return null;
    }
  } catch (error) {
    console.error(`Error getting user model settings for template ${templateId}:`, error);
    throw error;
  }
};

/**
 * Save a user's model settings for a specific template
 * @param {string} userId - The user ID
 * @param {string} templateId - The template ID
 * @param {Object} settings - The settings to save
 * @returns {Promise<Object>} The saved settings
 */
export const saveUserTemplateModelSettings = async (userId, templateId, settings) => {
  try {
    const settingsRef = doc(db, USER_MODEL_SETTINGS_COLLECTION, `${userId}_${templateId}`);
    
    const settingsData = {
      userId,
      templateId,
      ...settings,
      updatedAt: new Date().toISOString()
    };
    
    await setDoc(settingsRef, settingsData);
    
    return settingsData;
  } catch (error) {
    console.error(`Error saving user model settings for template ${templateId}:`, error);
    throw error;
  }
};

/**
 * Get user-specific model settings
 * @param {string} userId - The user ID
 * @returns {Promise<Object|null>} - The user's model settings or null if not found
 */
export const getUserModelSettings = async (userId) => {
  try {
    if (!userId) {
      console.error('getUserModelSettings called with no userId');
      return null;
    }

    const userSettingsRef = doc(db, 'user_model_settings', userId);
    const userSettingsDoc = await getDoc(userSettingsRef);
    
    if (userSettingsDoc.exists()) {
      const data = userSettingsDoc.data();
      console.log('Found user model settings:', data);
      return data;
    } else {
      console.log('No user model settings found for user:', userId);
      return null;
    }
  } catch (error) {
    console.error('Error getting user model settings:', error);
    return null;
  }
};

/**
 * Update user-specific model settings
 * @param {string} userId - The user ID
 * @param {Object} settings - The user's model settings
 * @returns {Promise<Object>} - The updated user settings
 */
export const updateUserModelSettings = async (userId, settings) => {
  try {
    if (!userId) {
      throw new Error('User ID is required to update model settings');
    }

    const userSettingsRef = doc(db, 'user_model_settings', userId);
    
    // Ensure we don't store empty API keys
    const sanitizedSettings = { ...settings };
    
    // Remove empty API keys to avoid overwriting existing ones
    Object.keys(sanitizedSettings).forEach(provider => {
      if (sanitizedSettings[provider] && sanitizedSettings[provider].apiKey === '') {
        delete sanitizedSettings[provider].apiKey;
      }
    });
    
    // Add timestamp
    const updateData = {
      ...sanitizedSettings,
      updatedAt: new Date().toISOString()
    };
    
    // Update or create the document
    await setDoc(userSettingsRef, updateData, { merge: true });
    
    console.log(`Updated model settings for user ${userId}`);
    return updateData;
  } catch (error) {
    console.error('Error updating user model settings:', error);
    throw error;
  }
};
